import * as React from 'react'

import { AttendanceShiftLocationTypeEnumValues } from 'generated/resources/datatable.graphql'
import {
  CreateAttendanceEditTimesheetRequestDocument,
  CreateAttendanceEditTimesheetRequestMutationVariables,
  TimesheetEditTimesheetRequestFragmentDoc,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Return } from 'modules/core/lib/asyncAction'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { modalTextByType } from 'modules/attendance/lib/editTimesheetRequestsDisplay'
import { trackDailyClockIn } from 'modules/attendance/lib/tracking'
import { DEFAULT_CLOCK } from 'modules/attendance/lib/utils'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import { RequestType, RequestTypes } from 'modules/attendance/store/models/EditTimesheetRequest'
import { LOCATION_TYPES } from 'modules/attendance/store/models/Shift'

import Form from './Form'
import ModalFooter from './ModalFooter'

interface CreateShiftRequest {
  date: moment.Moment
  hasBreaksEnabled?: boolean
  dayBreaks?: AttendanceBreakConfigurations[]
  lastShift?: TimesheetPageShiftFragment | undefined
  hasLocationEnabled?: boolean
}

interface UpdateShiftRequest {
  dayBreaks?: AttendanceBreakConfigurations[]
  shift?: TimesheetPageShiftFragment
  hasBreaksEnabled?: boolean
  hasLocationEnabled?: boolean
}

interface DeleteShiftRequest {
  shift?: TimesheetPageShiftFragment
  isDeleteRequest?: boolean
}

interface Props extends CreateShiftRequest, UpdateShiftRequest, DeleteShiftRequest {
  onCloseModal: () => void
  refreshTimesheet?: () => Promise<Return<void>>
  isResolve?: boolean
  employeeId: number
  showStartsNextDayCheckbox?: boolean
  hasForfaitJours?: boolean
}

const trackSubmit = ({
  requestType,
  shiftType,
  observations,
  isResolve,
}: {
  requestType: RequestType
  shiftType: string
  observations: string
  isResolve: boolean
}) => {
  switch (requestType) {
    case RequestTypes.createShift: {
      track(trackingEvents.timeTracking.addShiftRequestAddSubmit, {
        shift_type: shiftType,
        observations,
      })
      break
    }
    case RequestTypes.updateShift: {
      track(trackingEvents.timeTracking.addShiftRequestEditSubmit, {
        observations,
        resolve: isResolve,
      })
      break
    }
    case RequestTypes.deleteShift: {
      track(trackingEvents.timeTracking.addShiftRequestDeleteSubmit)
      break
    }
  }
}

const CreateRequestModal = ({
  shift,
  lastShift,
  onCloseModal,
  isDeleteRequest,
  date,
  hasBreaksEnabled,
  hasLocationEnabled,
  dayBreaks,
  refreshTimesheet,
  employeeId,
  isResolve = false,
  showStartsNextDayCheckbox,
  hasForfaitJours = false,
}: Props) => {
  const dayBreaksOptions = dayBreaks?.map(({ name, timeSettingsBreakConfigurationId }) => ({
    label: name,
    value: timeSettingsBreakConfigurationId || 0,
  }))

  let requestType: RequestType = RequestTypes.createShift
  if (shift) {
    requestType = RequestTypes.updateShift
  }
  if (isDeleteRequest) {
    requestType = RequestTypes.deleteShift
  }
  const isCreateShiftRequest = requestType === RequestTypes.createShift
  const { locationType } = lastShift || {}
  const defaultLocationType = hasLocationEnabled ? locationType || LOCATION_TYPES.OFFICE : null
  const lastShiftClockOutMoment = lastShift && AttendanceShiftUtils.clockOut(lastShift)
  const lastShiftClockOutTimeStr = lastShiftClockOutMoment
    ? dateToString(lastShiftClockOutMoment, 'ui_time')
    : DEFAULT_CLOCK
  const initialShiftDate =
    showStartsNextDayCheckbox && lastShift?.showPlusOneDay
      ? date.clone().add(1, 'days')
      : date.clone()
  const now = moment()
  const defaultValues: CreateAttendanceEditTimesheetRequestMutationVariables = {
    clockIn: shift ? AttendanceShiftUtils.clockInTimeStr(shift) : lastShiftClockOutTimeStr,
    clockOut: shift ? AttendanceShiftUtils.clockOutTimeStr(shift) : DEFAULT_CLOCK,
    locationType: AttendanceShiftLocationTypeEnumValues.find(
      (location) => location === defaultLocationType
    ),
    attendanceShiftId: shift?.id,
    referenceDate: dateToString(date, 'backend'),
    observations: shift?.observations,
    date: isCreateShiftRequest && date ? dateToString(initialShiftDate, 'backend') : shift?.date,
    employeeId: employeeId,
    requestType: requestType,
    workable: true,
    timeSettingsBreakConfigurationId: dayBreaksOptions?.length
      ? dayBreaksOptions[0]?.value
      : undefined,
  }
  const [createEditTimesheetRequest] = useMutation(CreateAttendanceEditTimesheetRequestDocument, {
    extractorFn: (data) => data.attendanceMutations.createAttendanceEditTimesheetRequest,
    update: (cache, { data }) => {
      const request =
        data?.attendanceMutations.createAttendanceEditTimesheetRequest.editTimesheetRequest
      cache.modify({
        id: `AttendanceEmployee:${employeeId}`,
        fields: {
          attendanceEditTimesheetRequests(existingConnection) {
            const newRequestRef = cache.writeFragment({
              data: request,
              fragment: TimesheetEditTimesheetRequestFragmentDoc,
            })

            return [...existingConnection, newRequestRef]
          },
        },
      })
    },
  })

  return (
    <FormProvider
      defaultValues={defaultValues}
      validate={(data) => {
        const errors: FormErrors<CreateAttendanceEditTimesheetRequestMutationVariables> = {}
        const [clockInHours, clockInMinutes] = data.clockIn?.split(':') ?? []
        const [clockOutHours, clockOutMinutes] = data.clockOut?.split(':') ?? []
        const shiftDate = moment(data.date)
        const clockIn = shiftDate
          .clone()
          .utcOffset(now.utcOffset())
          .hours(Number(clockInHours))
          .minutes(Number(clockInMinutes))
        const clockOut = shiftDate
          .clone()
          .utcOffset(now.utcOffset())
          .hours(Number(clockOutHours))
          .minutes(Number(clockOutMinutes))

        const isOvernightShift = clockOut.isBefore(clockIn)
        const clockInIsFuture = !!data.clockIn && clockIn.isAfter(now)
        const clockOutIsFuture =
          !!data.clockOut &&
          (isOvernightShift ? clockOut.clone().add(1, 'day').isAfter(now) : clockOut.isAfter(now))

        if (clockInIsFuture) {
          errors.clockIn = i18n.t('clock_in.edit_timesheet_requests.modal.errors.clock_in_future')
        }

        if (clockOutIsFuture) {
          errors.clockOut = i18n.t('clock_in.edit_timesheet_requests.modal.errors.clock_in_future')
        }

        return errors
      }}
      onSubmit={async (data, { setErrors }) => {
        const result = await createEditTimesheetRequest({
          variables: data,
        })

        const handled = handleResult(result, (errors) => {
          setErrors(errors)

          app.setFlash({
            type: 'wrong',
            message: errors?.base || errors?.messages || i18n.t('errors.generic_error'),
          })
        })

        if (handled.success && handled.data.editTimesheetRequest) {
          trackSubmit({
            requestType,
            shiftType: !data.workable ? 'break' : 'shift',
            observations: data.observations ? 'filled' : 'empty',
            isResolve,
          })
          if (requestType === RequestTypes.createShift && data.workable && hasForfaitJours) {
            trackDailyClockIn('timesheet_daily_desktop')
          }
          refreshTimesheet && refreshTimesheet()
          onCloseModal()
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }

        return handled
      }}
    >
      <Modal
        title={modalTextByType(requestType).title}
        description={modalTextByType(requestType).description}
        onClose={onCloseModal}
        footer={<ModalFooter onCloseModal={onCloseModal} />}
      >
        <Form
          date={date}
          isCreateShiftRequest={isCreateShiftRequest}
          isDeleteRequest={isDeleteRequest}
          hasBreaksEnabled={hasBreaksEnabled}
          hasLocationEnabled={hasLocationEnabled}
          dayBreaksOptions={dayBreaksOptions}
          showStartsNextDayCheckbox={showStartsNextDayCheckbox}
        />
      </Modal>
    </FormProvider>
  )
}

export default observer(CreateRequestModal)
